import { createColumnHelper } from '@tanstack/react-table';
import { useEffect, useState } from 'react';
import { IPublisherLink, LinkDisplayStatus } from '../../models/link.model';
import { IMergedHeadersReturn } from './index.headers';
import { usePageSettings } from '@monorepo/tools/src/lib/hooks/tools/use-page-settings';
import { ButtonDimensions, Directions, IconButton } from '@monorepo/base/src/components/buttons/buttons';
import { Tag, TagTypes } from '@monorepo/base/src/components/tag/tag';
import { Tooltip } from '@monorepo/controlled/src/components/tooltip/tooltip/tooltip';
import { CellTypes, renderCell } from '../../helpers/cells.helper';
import styles from './my-links.headers.module.scss';

const columnHelper = createColumnHelper<IPublisherLink>();

/** Shown when {@code allowSelfLinkGeneration} is false; table stays read-only (copy/export still work). */
const SELF_SERVE_LINKS_DISABLED_TOOLTIP =
	'Self-serve link actions are not available for this account. Contact your Account Manager for assistance.';

interface IUseMyLinksHeadersOptions {
	onStop?: (link: IPublisherLink) => void;
	onGenerate?: (link: IPublisherLink) => void;
	/** From selected publisher account; when false, Manage column is read-only (tooltips explain). */
	canManageSelfServeLinks: boolean;
}

const displayStatusToTagType: Record<LinkDisplayStatus, string> = {
	Active: TagTypes.Success,
	Inactive: TagTypes.Pending,
	Stopped: TagTypes.Paused,
	'Stopped by Manager': TagTypes.Caution,
};

/** Darker fills than default Tag styles so status reads clearly on the table. */
const myLinksStatusTagBackground: Record<LinkDisplayStatus, string> = {
	Active: '#d1fae5',
	Inactive: '#e0e7ff',
	Stopped: '#e4e7ec',
	'Stopped by Manager': '#fed7aa',
};

const myLinksStatusTagBorder: Record<LinkDisplayStatus, string> = {
	Active: '1px solid #6ee7b7',
	Inactive: '1px solid #a5b4fc',
	Stopped: '1px solid #d0d5dd',
	'Stopped by Manager': '1px solid #fb923c',
};

function MyLinksTransportCell({
	link,
	onStop,
	onGenerate,
	canManageSelfServeLinks,
}: {
	link: IPublisherLink;
	onStop?: (l: IPublisherLink) => void;
	onGenerate?: (l: IPublisherLink) => void;
	canManageSelfServeLinks: boolean;
}) {
	const { displayStatus } = link;

	if (displayStatus === 'Active') {
		if (!canManageSelfServeLinks) {
			return (
				<div className={styles.transportCell}>
					<Tooltip direction={Directions.FromLeft} content={SELF_SERVE_LINKS_DISABLED_TOOLTIP} bottom="8px">
						<span className={styles.transportBtnWrap}>
							<IconButton
								dimension={ButtonDimensions.Small}
								disabled
								size="22px"
								color="#fda29b"
								aria-label="Remove link unavailable">
								pause-circle
							</IconButton>
						</span>
					</Tooltip>
				</div>
			);
		}
		const btn = (
			<IconButton
				dimension={ButtonDimensions.Small}
				onClick={() => onStop?.(link)}
				size="22px"
				color="#d92d20"
				aria-label="Remove link">
				pause-circle
			</IconButton>
		);
		return (
			<div className={styles.transportCell}>
				<Tooltip direction={Directions.FromLeft} content="Remove link" bottom="8px">
					<span className={styles.transportBtnWrap}>{btn}</span>
				</Tooltip>
			</div>
		);
	}

	if (displayStatus === 'Stopped') {
		if (!canManageSelfServeLinks) {
			return (
				<div className={styles.transportCell}>
					<Tooltip direction={Directions.FromLeft} content={SELF_SERVE_LINKS_DISABLED_TOOLTIP} bottom="8px">
						<span className={styles.transportBtnWrap}>
							<IconButton
								dimension={ButtonDimensions.Small}
								disabled
								size="22px"
								color="#84caff"
								aria-label="Restore link unavailable">
								play-circle
							</IconButton>
						</span>
					</Tooltip>
				</div>
			);
		}
		const btn = (
			<IconButton
				dimension={ButtonDimensions.Small}
				onClick={() => onGenerate?.(link)}
				size="22px"
				color="#027a48"
				aria-label="Restore link">
				play-circle
			</IconButton>
		);
		return (
			<div className={styles.transportCell}>
				<Tooltip direction={Directions.FromLeft} content="Restore link" bottom="8px">
					<span className={styles.transportBtnWrap}>{btn}</span>
				</Tooltip>
			</div>
		);
	}

	if (displayStatus === 'Stopped by Manager') {
		return (
			<div className={styles.transportCell}>
				<Tooltip direction={Directions.FromLeft} content="This link was stopped by your account manager" bottom="8px">
					<span className={styles.transportBtnWrap}>
						<IconButton dimension={ButtonDimensions.Small} disabled size="22px" color="#fda29b" aria-label="Stopped by manager">
							pause-circle
						</IconButton>
					</span>
				</Tooltip>
			</div>
		);
	}

	return (
		<div className={styles.transportCell}>
			<Tooltip direction={Directions.FromLeft} content="Campaign currently unavailable" bottom="8px">
				<span className={styles.transportBtnWrap}>
					<IconButton dimension={ButtonDimensions.Small} disabled size="22px" color="#f97066" aria-label="Link inactive">
						pause-circle
					</IconButton>
				</span>
			</Tooltip>
		</div>
	);
}

function MyLinksCopyIconButton({ url, inactiveCampaign }: { url: string; inactiveCampaign?: boolean }) {
	const [copied, setCopied] = useState(false);

	const handleCopy = () => {
		navigator.clipboard.writeText(url).then(() => {
			setCopied(true);
			setTimeout(() => setCopied(false), 2000);
		});
	};

	const button = (
		<span title={copied ? 'Copied' : 'Copy link'} className={styles.copyIconWrap}>
			<IconButton
				dimension={ButtonDimensions.Small}
				onClick={handleCopy}
				size="18px"
				color={copied ? '#12b76a' : '#667085'}
				aria-label={copied ? 'Copied to clipboard' : 'Copy tracking URL to clipboard'}>
				{copied ? 'check-circle' : 'copy-06'}
			</IconButton>
		</span>
	);

	if (inactiveCampaign) {
		return (
			<Tooltip direction={Directions.FromLeft} content={'Campaign currently unavailable'} bottom={'8px'}>
				<span className={styles.copyIconWrap}>{button}</span>
			</Tooltip>
		);
	}

	return button;
}

function MyLinksTrackingUrlCell({ link }: { link: IPublisherLink }) {
	const { displayStatus, trackingUrl } = link;
	const url = (trackingUrl ?? '').trim();
	const showDash = displayStatus === 'Stopped' || displayStatus === 'Stopped by Manager' || url.length === 0;
	if (showDash) {
		return (
			<div className={styles.linkCell}>
				<span className={styles.actionCellMuted}>—</span>
			</div>
		);
	}
	const inactiveCampaign = displayStatus === 'Inactive';
	return (
		<div className={styles.linkCell}>
			<div className={styles.linkFieldShell}>
				<div className={styles.linkTextClip}>
					<div className={styles.linkTooltipWrapper}>
						<Tooltip direction={Directions.FromLeft} content={url} bottom="8px" maxHeight={220}>
							<span className={styles.linkText}>{url}</span>
						</Tooltip>
					</div>
				</div>
				<div className={styles.linkCopySlot}>
					<MyLinksCopyIconButton url={url} inactiveCampaign={inactiveCampaign} />
				</div>
			</div>
		</div>
	);
}

export const useMyLinksHeaders = (options: IUseMyLinksHeadersOptions): IMergedHeadersReturn<IPublisherLink> => {
	const { onStop, onGenerate, canManageSelfServeLinks } = options;
	const { tableStore } = usePageSettings();

	useEffect(() => {
		if (!tableStore) {
			return;
		}
		const colVis = {
			id: false,
			transport: true,
			link: true,
			domain: true,
			geo: true,
			linkType: true,
			createdAt: true,
			displayStatus: true,
		};
		tableStore.setDefaultColumnVisibility(colVis);
		const prev = tableStore.getColumnVisibility() || {};
		if (Object.keys(prev).length === 0) {
			tableStore.setColumnVisibility(colVis);
		}
	}, [tableStore]);

	const columns = [
		columnHelper.accessor('id', {
			header: 'ID',
			size: 72,
		}),
		columnHelper.display({
			id: 'transport',
			header: 'Manage',
			size: 52,
			minSize: 44,
			maxSize: 56,
			cell: ({ row }) => (
				<MyLinksTransportCell
					link={row.original}
					onStop={onStop}
					onGenerate={onGenerate}
					canManageSelfServeLinks={canManageSelfServeLinks}
				/>
			),
		}),
		columnHelper.display({
			id: 'link',
			header: 'Link',
			size: 210,
			minSize: 110,
			maxSize: 280,
			cell: ({ row }) => <MyLinksTrackingUrlCell link={row.original} />,
		}),
		columnHelper.accessor('domain', {
			header: () => <span className={styles.domainHeader}>Domain</span>,
			meta: { columnPickerLabel: 'Domain' },
			size: 188,
			minSize: 120,
			maxSize: 240,
			cell: ({ row }) => {
				const link = row.original;
				const clip = `${styles.cellClip} ${styles.cellClipDomain}`;
				if (link.displayStatus === 'Active') {
					return <div className={clip}>{renderCell(CellTypes.Domain, row, 'domain')}</div>;
				}
				return <div className={clip}>{row.getValue('domain') as string}</div>;
			},
		}),
		columnHelper.accessor('geo', {
			header: 'Geo',
			size: 176,
			minSize: 140,
			maxSize: 240,
			cell: ({ row }) => <div className={styles.cellClip}>{renderCell(CellTypes.Geo, row, 'geo')}</div>,
		}),
		columnHelper.accessor('linkType', {
			header: 'Link Type',
			size: 76,
			minSize: 56,
			maxSize: 96,
			cell: ({ getValue }) => <div className={styles.cellClip}>{String(getValue())}</div>,
		}),
		columnHelper.accessor('createdAt', {
			header: 'Created At',
			size: 118,
			minSize: 104,
			maxSize: 140,
			cell: ({ row }) => {
				const val = row.getValue('createdAt');
				if (!val) {
					return null;
				}
				return <div className={styles.cellClip}>{renderCell(CellTypes.Date, row, 'createdAt')}</div>;
			},
		}),
		columnHelper.accessor('displayStatus', {
			header: 'Status',
			size: 172,
			minSize: 148,
			maxSize: 220,
			cell: ({ getValue }) => {
				const status = getValue() as LinkDisplayStatus;
				const tagType = displayStatusToTagType[status];
				return (
					<Tag type={tagType} backgroundColor={myLinksStatusTagBackground[status]} border={myLinksStatusTagBorder[status]}>
						<span>{status}</span>
					</Tag>
				);
			},
		}),
	];

	return { columns };
};
